import { useEffect, useRef, type RefObject } from 'react';
import { useStage } from './stage';

/**
 * A section's content comes alive once, when the section takes ownership of Mr. Clippy's eyes (the most visible
 * section ≥ 40% of its height in view; the stage dispatches `fx-guide-owner`). One voice at a time: the content
 * animation and his reaction to it start together, so two sections never fight over his eyes. Without the stage
 * (a page outside GuideStage) the section enters at ≥ 40% visible.
 */
export function useSectionEnter(ref: RefObject<HTMLElement | null>, sel: string, fn: () => void) {
  const stage = useStage();
  const done = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const run = () => { if (done.current) return; done.current = true; fnRef.current(); };
    if (stage) {
      if (stage.owner() === sel) { run(); return; }
      const on = (e: Event) => { if ((e as CustomEvent<string>).detail === sel) run(); };
      document.addEventListener('fx-guide-owner', on);
      return () => document.removeEventListener('fx-guide-owner', on);
    }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && e.intersectionRatio >= 0.4) { io.disconnect(); run(); } }, { threshold: [0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, sel, stage]);
}
